/**
 * Impairment Analysis - stage 2 of the impairment workflow.
 *
 * Stage 1 (ACC assessment upload) extracts the notes into a structured summary.
 * Here the assessor adds the history and examination taken at the consultation,
 * and the whole picture is re-analysed: proposed ratings with the method used,
 * the combined whole person figure, apportionment, duplication and a draft
 * conclusion - all for review. Nothing here is a final rating.
 *
 * SECURITY: All fetch calls include the JWT from sessionStorage.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://clinhx-backend-v2.onrender.com/api';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SEVERITY_COLOUR = {
  critical: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
  major: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
  minor: { bg: '#f8fafc', border: '#e2e8f0', text: '#475569' },
  info: { bg: '#f0f9ff', border: '#bae6fd', text: '#075985' },
};

const CATEGORY_LABEL = {
  accuracy: 'Accuracy',
  consistency: 'Consistency',
  repetition: 'Duplication',
  completeness: 'Completeness',
};

// Frontend field key to the domain name the analyser uses.
// Sections 3 to 10 are the history: chronology, other conditions, previous
// reports, personal and psychiatric history, treatments and family history.
// They carry most of the report, so they get a much taller box, and grow
// further when the text is long enough to need it.
const HISTORY_SECTIONS = new Set(['3', '4', '5', '6', '7', '8', '9', '10']);

function sectionBoxHeight(sectionId, text) {
  const base = HISTORY_SECTIONS.has(String(sectionId)) ? 460 : 160;
  // Roughly one line per 90 characters at this width, 22px a line.
  const newline = String.fromCharCode(10);
  const lineCount = String(text || '').split(newline).reduce(
    (n, line) => n + Math.max(1, Math.ceil(line.length / 90)), 0);
  return Math.min(1200, Math.max(base, lineCount * 22 + 60));
}

const DOMAIN_KEY_MAP = {
  adl: 'activitiesOfDailyLiving',
  social: 'socialFunctioning',
  concentration: 'concentration',
  adaptation: 'adaptation',
};

const DOMAIN_FIELDS = [
  {
    key: 'personalCircumstances',
    heading: 'Personal circumstances',
    placeholder: 'Living situation, dependants, work, housing.',
  },
  {
    key: 'adl',
    heading: 'Activities of daily living',
    placeholder: 'Sleep, self-care, meals, household tasks, shopping, driving, relationships, hobbies.',
  },
  {
    key: 'social',
    heading: 'Social functioning',
    placeholder: 'Contact with family and friends, groups, neighbours, ability to form new contacts, social norms.',
  },
  {
    key: 'concentration',
    heading: 'Concentration, persistence and pace',
    placeholder: 'Focus, planning, organisation, decision-making, completing tasks, administration.',
  },
  {
    key: 'adaptation',
    heading: 'Adaptation / decompensation',
    placeholder: 'Reaction to stress and to plans changing, frequency and duration of bad days, what happens on them.',
  },
];

const ImpairmentAnalysis = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [clinicalHistory, setClinicalHistory] = useState('');
  const [examination, setExamination] = useState('');
  const [domainNotes, setDomainNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  // Load the assessment and any addendum saved earlier.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [aRes, addRes] = await Promise.all([
          fetch(`${API_URL}/acc-assessments/${id}`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/acc-assessments/${id}/addendum`, { headers: getAuthHeaders() }),
        ]);
        if (!aRes.ok) throw new Error('Could not load this assessment.');
        const aData = await aRes.json();
        if (cancelled) return;
        setAssessment(aData.assessment || aData);

        if (addRes.ok) {
          const add = await addRes.json();
          if (cancelled) return;
          setClinicalHistory(add.clinicalHistory || '');
          setExamination(add.examination || '');
          setDomainNotes(add.domainNotes || {});
          setSavedAt(add.updatedAt || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/acc-assessments/${id}/addendum`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ clinicalHistory, examination, domainNotes }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not save.');
      }
      const data = await res.json();
      setSavedAt(data.updatedAt);
      setDirty(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [id, clinicalHistory, examination, domainNotes]);



  // ---- Report editor -------------------------------------------------------
  const [tab, setTab] = useState('assessment');
  const [sections, setSections] = useState(null);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionDrafts, setSectionDrafts] = useState({});
  const [savingSection, setSavingSection] = useState(null);

  const loadSections = useCallback(async () => {
    setSectionsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/acc-assessments/${id}/report`, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not build the report.');
      setSections(data.sections || []);
      // Drafts start as whatever is currently in the report.
      const drafts = {};
      (data.sections || []).forEach((s) => { drafts[s.id] = s.body; });
      setSectionDrafts(drafts);
    } catch (err) {
      setError(err.message);
    } finally {
      setSectionsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (tab === 'report' && sections === null && !sectionsLoading) loadSections();
  }, [tab, sections, sectionsLoading, loadSections]);

  const saveSection = useCallback(async (sectionId, body) => {
    setSavingSection(sectionId);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/acc-assessments/${id}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ sectionId, body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save the section.');
      // Rebuild so the change is reflected exactly as the document will render it.
      await loadSections();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSection(null);
    }
  }, [id, loadSections]);

  const downloadReport = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/acc-assessments/${id}/download`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Download failed.');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACC_Assessment_${(assessment?.patient?.name || 'report').replace(/\s+/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  }, [id, assessment]);


  // Analyse, assemble the report, file it in Dropbox and download it - one action.
  const [completed, setCompleted] = useState(null);
  const [dropboxRoot, setDropboxRoot] = useState(
    () => localStorage.getItem('clinhx.dropboxRoot') || '');
  const analyseAndDownload = useCallback(async () => {
    setAnalysing(true);
    setError(null);
    setCompleted(null);
    try {
      const res = await fetch(`${API_URL}/acc-assessments/${id}/analyse-and-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ clinicalHistory, examination, domainNotes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'The analysis failed.');

      // Hand the document to the browser.
      const bytes = Uint8Array.from(atob(data.document), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setCompleted(data);
      setAnalysis(data.analysis);
      // Section 11 has been rewritten, so the editor must be rebuilt.
      setSections(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalysing(false);
    }
  }, [id, clinicalHistory, examination, domainNotes]);

  // Word's Office URI scheme needs LITERAL pipes: ms-word:ofe|u|<target>.
  // Assigning to window.location.href percent-encodes them to %7C, which Word
  // rejects with "Office doesn't recognise the command it was given". Setting
  // the attribute on an anchor and clicking it leaves them alone.
  const launchWord = useCallback((target) => {
    const a = document.createElement('a');
    a.setAttribute('href', 'ms-word:ofe|u|' + target);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // Prefer the file synced to the local Dropbox folder: Word can open it
  // read/write and save back, whereas a Dropbox download URL is a short-lived
  // redirect it can at best open read-only.
  const openInWord = useCallback(() => {
    const localRoot = (localStorage.getItem('clinhx.dropboxRoot') || '').trim();
    if (localRoot && completed?.dropbox?.path) {
      const backslash = String.fromCharCode(92);
      const parts = completed.dropbox.path.split('/').filter(Boolean);
      let root = localRoot;
      while (root.endsWith('/') || root.endsWith(backslash)) root = root.slice(0, -1);
      launchWord(root + backslash + parts.join(backslash));
      return;
    }
    if (completed?.dropbox?.link) launchWord(completed.dropbox.link);
  }, [completed, launchWord]);

  const onHistoryChange = (v) => { setClinicalHistory(v); setDirty(true); };
  const onExamChange = (v) => { setExamination(v); setDirty(true); };
  const onDomainChange = (key, v) => {
    setDomainNotes((prev) => ({ ...prev, [key]: v }));
    setDirty(true);
  };
  const hasDomainNotes = Object.values(domainNotes).some((v) => String(v || '').trim());
  const canAnalyse = (clinicalHistory.trim() || examination.trim() || hasDomainNotes) && !analysing;

  if (loading) {
    return <div style={{ padding: '32px' }}>Loading assessment…</div>;
  }

  const patientName = assessment?.patient?.name || 'Patient';
  const label = { display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' };
  const textarea = {
    width: '100%', minHeight: '180px', padding: '12px', fontSize: '14px',
    fontFamily: 'inherit', lineHeight: 1.5, borderRadius: '8px',
    border: '1px solid #cbd5e1', resize: 'vertical', boxSizing: 'border-box',
  };
  const card = {
    background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px',
    padding: '20px', marginBottom: '20px',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/acc-assessment')}
        style={{
          background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
          fontSize: '14px', padding: 0, marginBottom: '12px',
        }}
      >
        ← Back to assessments
      </button>

      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
        Impairment analysis
      </h1>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: '24px', fontSize: '14px' }}>
        {patientName}
        {assessment?.injuryType ? ` — ${assessment.injuryType.replace(/_/g, ' ').toLowerCase()}` : ''}
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
          padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {/* Two stages: correct the generated summary, then add the assessment. */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
        {[['report', 'Report'], ['assessment', 'History, examination & analysis']].map(([key, text]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: tab === key ? 600 : 400,
              color: tab === key ? '#4f46e5' : '#64748b',
              borderBottom: tab === key ? '2px solid #4f46e5' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {text}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', paddingBottom: '6px' }}>
          <button
            onClick={downloadReport}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1',
              background: 'white', cursor: 'pointer', fontSize: '13px',
            }}
          >
            Download report
          </button>
        </div>
      </div>

      {tab === 'report' && (
        <div style={card}>
          <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0, marginBottom: '4px' }}>
            Generated report
          </h2>
          <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
            Summarised from the records provided. Correct anything wrong here before you see the
            claimant — the download carries your edits. Clearing an edit restores the generated text.
          </p>

          {sectionsLoading && <p style={{ fontSize: '14px' }}>Building the report…</p>}

          {sections && sections.map((s) => {
            const draft = sectionDrafts[s.id] ?? s.body;
            const changed = draft !== s.body;
            return (
              <div key={s.id} style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ ...label, marginBottom: 0 }} htmlFor={`section-${s.id}`}>
                    {s.heading}
                  </label>
                  {s.edited && (
                    <span style={{
                      fontSize: '11px', color: '#4f46e5', background: '#eef2ff',
                      padding: '2px 8px', borderRadius: '9999px',
                    }}>
                      edited
                    </span>
                  )}
                </div>
                <textarea
                  id={`section-${s.id}`}
                  style={{ ...textarea, minHeight: `${sectionBoxHeight(s.id, draft)}px` }}
                  value={draft}
                  onChange={(e) => setSectionDrafts((prev) => ({ ...prev, [s.id]: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => saveSection(s.id, draft)}
                    disabled={!changed || savingSection === s.id}
                    style={{
                      padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1',
                      background: 'white', fontSize: '13px',
                      cursor: !changed || savingSection === s.id ? 'not-allowed' : 'pointer',
                      opacity: !changed || savingSection === s.id ? 0.5 : 1,
                    }}
                  >
                    {savingSection === s.id ? 'Saving…' : 'Save section'}
                  </button>
                  {s.edited && (
                    <button
                      onClick={() => saveSection(s.id, null)}
                      disabled={savingSection === s.id}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', border: 'none',
                        background: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer',
                      }}
                    >
                      Restore generated text
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {tab === 'assessment' && (<>
      <div style={card}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0, marginBottom: '4px' }}>
          History and examination at assessment
        </h2>
        <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
          The summary above this was extracted from the records. What you write here is your own,
          taken at the consultation, and is kept separate so the source of every statement stays clear.
        </p>

        <div style={{ marginBottom: '20px' }}>
          <label style={label} htmlFor="clinical-history">Current symptoms and history</label>
          <textarea
            id="clinical-history"
            style={textarea}
            value={clinicalHistory}
            onChange={(e) => onHistoryChange(e.target.value)}
            placeholder="Symptoms now, functional impact, activities of daily living, treatment since the records end, stability."
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={label} htmlFor="examination">Examination findings</label>
          <textarea
            id="examination"
            style={textarea}
            value={examination}
            onChange={(e) => onExamChange(e.target.value)}
            placeholder="Record the measurements the rating method needs — ranges of movement, strength grades, specific tests. Anything not recorded here cannot be rated."
          />
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '24px', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0, marginBottom: '4px' }}>
            Chapter 14 domains
          </h2>
          <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
            Record what the claimant described in each domain. Analyse proposes the Class and
            percentage for each from what you write here — a domain left empty is not rated.
          </p>

          {DOMAIN_FIELDS.map((d) => (
            <div key={d.key} style={{ marginBottom: '20px' }}>
              <label style={label} htmlFor={`domain-${d.key}`}>{d.heading}</label>
              <textarea
                id={`domain-${d.key}`}
                style={{ ...textarea, minHeight: '140px' }}
                value={domainNotes[d.key] || ''}
                onChange={(e) => onDomainChange(d.key, e.target.value)}
                placeholder={d.placeholder}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={save}
            disabled={saving || !dirty}
            style={{
              padding: '10px 18px', borderRadius: '8px', border: '1px solid #cbd5e1',
              background: 'white', cursor: saving || !dirty ? 'not-allowed' : 'pointer',
              opacity: saving || !dirty ? 0.6 : 1, fontSize: '14px',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>

          <button
            onClick={analyseAndDownload}
            disabled={!canAnalyse}
            style={{
              padding: '10px 20px', borderRadius: '8px', border: 'none',
              background: canAnalyse ? '#4f46e5' : '#94a3b8', color: 'white',
              cursor: canAnalyse ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: 600,
            }}
          >
            {analysing ? 'Analysing…' : 'Analyse & download report'}
          </button>

          <span style={{ fontSize: '13px', color: '#64748b' }}>
            {dirty
              ? 'Unsaved changes — Analyse uses what is on screen.'
              : savedAt
                ? `Saved ${new Date(savedAt).toLocaleString('en-NZ')}`
                : ''}
          </span>
        </div>

        {analysing && (
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: 0 }}>
            Proposing ratings and reviewing the report. This can take a couple of minutes.
          </p>
        )}
      </div>

      </>)}


      {completed && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '20px',
        }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#166534', marginBottom: '6px' }}>
            Report complete — {completed.fileName} downloaded
          </div>
          <div style={{ fontSize: '13px', color: '#166534' }}>
            {completed.dropbox?.saved
              ? `Saved to Dropbox at ${completed.dropbox.path}.`
              : (completed.dropbox?.error || 'Not saved to Dropbox.')}
          </div>
          {completed.dropbox?.link && (
            <button
              onClick={openInWord}
              style={{
                marginTop: '10px', padding: '8px 16px', borderRadius: '8px',
                border: '1px solid #86efac', background: 'white', color: '#166534',
                cursor: 'pointer', fontSize: '13px', fontWeight: 500,
              }}
            >
              Open in Word
            </button>
          )}
          {completed.dropbox?.saved && (
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '12px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                Dropbox folder on this PC — set once, so Open in Word opens the synced file
              </label>
              <input
                type="text"
                value={dropboxRoot}
                onChange={(e) => {
                  setDropboxRoot(e.target.value);
                  localStorage.setItem('clinhx.dropboxRoot', e.target.value);
                }}
                placeholder={String.fromCharCode(67, 58, 92) + 'Users' + String.fromCharCode(92) + 'you' + String.fromCharCode(92) + 'Dropbox'}
                style={{
                  width: '100%', maxWidth: '420px', padding: '8px 10px', fontSize: '13px',
                  borderRadius: '6px', border: '1px solid #86efac', boxSizing: 'border-box',
                }}
              />
              <p style={{ fontSize: '12px', color: '#15803d', margin: '6px 0 0' }}>
                {dropboxRoot.trim()
                  ? 'Open in Word will open the synced copy, so your edits save straight back to Dropbox.'
                  : 'Until this is set, Open in Word tries the Dropbox web copy, which Word may refuse or open read-only. '
                    + 'You can also open the file from your Dropbox folder or the download that just landed.'}
              </p>
            </div>
          )}
          <p style={{ fontSize: '12px', color: '#15803d', marginBottom: 0, marginTop: '10px' }}>
            Section 11 has been rewritten with the analysis. Review the whole report before sending.
          </p>
        </div>
      )}

      {analysis && (
        <>
          {analysis.partialErrors?.length > 0 && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
              padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px',
            }}>
              <strong>Some passes did not complete:</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                {analysis.partialErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          <div style={card}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0 }}>Proposed conclusion</h2>
            {analysis.conclusion?.wholePersonImpairment != null && (
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b', margin: '8px 0' }}>
                {analysis.conclusion.wholePersonImpairment}%
                <span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b', marginLeft: '8px' }}>
                  whole person impairment
                </span>
              </div>
            )}
            <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#334155' }}>
              {analysis.conclusion?.text}
            </p>
            {analysis.conclusion?.outstanding?.length > 0 && (
              <div style={{
                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
                padding: '12px 16px', marginTop: '12px',
              }}>
                <strong style={{ fontSize: '13px', color: '#92400e' }}>
                  Needed before these can be rated:
                </strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: '20px', fontSize: '13px', color: '#92400e' }}>
                  {analysis.conclusion.outstanding.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0, fontStyle: 'italic' }}>
              A draft for you to accept, change or reject — not a final rating.
            </p>
          </div>

          <div style={card}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0, marginBottom: '4px' }}>
              Proposed ratings
            </h2>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0 }}>
              Each figure is proposed by AI with the method it applied. The combination is
              calculated exactly, not by the model.
            </p>

            {(analysis.combination?.rateable || []).map((c, i) => (
              <div key={i} style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginTop: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <strong style={{ fontSize: '14px' }}>{c.label}</strong>
                  <strong style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{c.percent}%</strong>
                </div>
                {c.method && (
                  <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
                    Method: {c.method}
                  </div>
                )}
                {c.evidence && (
                  <div style={{
                    fontSize: '13px', color: '#475569', marginTop: '6px',
                    borderLeft: '3px solid #e2e8f0', paddingLeft: '10px', fontStyle: 'italic',
                  }}>
                    {c.evidence}
                  </div>
                )}
                {c.reasoning && (
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>
                    {c.reasoning}
                  </div>
                )}
              </div>
            ))}

            {(analysis.combination?.unrateable || []).map((c, i) => (
              <div key={'u' + i} style={{
                borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginTop: '14px', opacity: 0.85,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <strong style={{ fontSize: '14px' }}>{c.label}</strong>
                  <span style={{ fontSize: '13px', color: '#92400e', whiteSpace: 'nowrap' }}>not rated</span>
                </div>
                {c.missing?.length > 0 && (
                  <div style={{ fontSize: '13px', color: '#92400e', marginTop: '4px' }}>
                    Needs: {c.missing.join('; ')}
                  </div>
                )}
              </div>
            ))}

            {analysis.combination?.working?.length > 0 && (
              <details style={{ marginTop: '16px' }}>
                <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#475569' }}>
                  Show the combination working
                </summary>
                <pre style={{
                  background: '#f8fafc', padding: '12px', borderRadius: '8px', fontSize: '12px',
                  overflowX: 'auto', marginTop: '8px',
                }}>
                  {analysis.combination.working.join('\n')}
                </pre>
              </details>
            )}
          </div>


          {analysis.mentalDomains && (
            <div style={card}>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0, marginBottom: '4px' }}>
                Chapter 14 domain ratings
              </h2>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0 }}>
                Proposed against{' '}
                {analysis.mentalDomains.bandTableSource || 'the supplied Class criteria'}.
                Every percentage is checked against its Class band in code.
              </p>

              {analysis.mentalDomains.issues?.length > 0 && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
                  padding: '12px 14px', marginBottom: '14px',
                }}>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#92400e' }}>
                    {analysis.mentalDomains.issues.map((iss, i) => <li key={i}>{iss.message}</li>)}
                  </ul>
                </div>
              )}

              {analysis.mentalDomains.domains.map((d, i) => {
                const field = DOMAIN_FIELDS.find((f) => DOMAIN_KEY_MAP[f.key] === d.domain);
                return (
                  <div key={i} style={{
                    borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginTop: '14px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                      <strong style={{ fontSize: '14px' }}>{field ? field.heading : d.domain}</strong>
                      <strong style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                        {d.classStated ? `Class ${d.classStated}` : 'not rated'}
                        {d.percent != null ? ` — ${d.percent}%` : ''}
                      </strong>
                    </div>
                    {d.percentAdjustedFrom != null && (
                      <div style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                        Adjusted from {d.percentAdjustedFrom}% to sit inside the Class band.
                      </div>
                    )}
                    {d.ratingSentence && (
                      <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#334155', marginBottom: '6px' }}>
                        {d.ratingSentence}
                      </p>
                    )}
                    {d.evidence && (
                      <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#475569', margin: '6px 0' }}>
                        {d.evidence}
                      </p>
                    )}
                    {d.exclusionSentence && (
                      <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#475569', margin: '6px 0' }}>
                        {d.exclusionSentence}
                      </p>
                    )}
                    {d.missing?.length > 0 && (
                      <div style={{ fontSize: '13px', color: '#92400e', marginTop: '6px' }}>
                        Needs: {d.missing.join('; ')}
                      </div>
                    )}
                  </div>
                );
              })}

              <div style={{
                borderTop: '2px solid #e2e8f0', marginTop: '18px', paddingTop: '14px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <strong style={{ fontSize: '14px' }}>Overall mental injury impairment</strong>
                  <strong style={{ fontSize: '18px' }}>
                    {analysis.mentalDomains.overallPercent != null
                      ? `${analysis.mentalDomains.overallPercent}%`
                      : 'not proposed'}
                  </strong>
                </div>
                {analysis.mentalDomains.overallReasoning && (
                  <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#475569', marginBottom: 0 }}>
                    {analysis.mentalDomains.overallReasoning}
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', marginBottom: 0 }}>
                  The domain ratings are not added, averaged or combined; the overall figure is a
                  matter of clinical judgement.
                </p>
              </div>
            </div>
          )}
          {analysis.apportionmentDraft && (
            <div style={card}>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0 }}>Apportionment draft</h2>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0 }}>
                Conclusion: {analysis.apportionmentDraft.conclusion}
              </p>
              <div style={{
                whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: 1.6,
                background: '#f8fafc', padding: '14px', borderRadius: '8px', color: '#334155',
              }}>
                {analysis.apportionmentDraft.draft}
              </div>
              {analysis.apportionmentDraft.gaps?.length > 0 && (
                <div style={{ fontSize: '13px', color: '#92400e', marginTop: '12px' }}>
                  <strong>Still needed:</strong> {analysis.apportionmentDraft.gaps.join('; ')}
                </div>
              )}
            </div>
          )}

          <div style={card}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: 0, marginBottom: '4px' }}>
              Review findings
            </h2>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0 }}>
              Duplication, internal consistency and completeness across the assembled report.
            </p>

            {(!analysis.findings || analysis.findings.length === 0) ? (
              <p style={{ fontSize: '14px', color: '#475569' }}>Nothing flagged.</p>
            ) : (
              analysis.findings.map((f) => {
                const c = SEVERITY_COLOUR[f.severity] || SEVERITY_COLOUR.minor;
                return (
                  <div key={f.id} style={{
                    background: c.bg, border: '1px solid ' + c.border, borderRadius: '8px',
                    padding: '12px 14px', marginBottom: '10px',
                  }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                        color: c.text, letterSpacing: '0.03em',
                      }}>
                        {f.severity}
                      </span>
                      <span style={{ fontSize: '11px', color: c.text, opacity: 0.8 }}>
                        {CATEGORY_LABEL[f.category] || f.category}
                      </span>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: c.text, marginTop: '4px' }}>
                      {f.title}
                    </div>
                    {f.detail && (
                      <div style={{ fontSize: '13px', color: c.text, marginTop: '4px', opacity: 0.95 }}>
                        {f.detail}
                      </div>
                    )}
                    {f.quote && (
                      <div style={{
                        fontSize: '12px', marginTop: '8px', whiteSpace: 'pre-wrap',
                        borderLeft: '3px solid ' + c.border, paddingLeft: '10px',
                        fontStyle: 'italic', color: c.text, opacity: 0.9,
                      }}>
                        {f.quote}
                      </div>
                    )}
                    {f.suggestion && (
                      <div style={{ fontSize: '13px', color: c.text, marginTop: '8px' }}>
                        <strong>Suggested:</strong> {f.suggestion}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ImpairmentAnalysis;
